// The product details panel is sticky and vertically centred from 990px (snippets/product-page-
// styles.liquid). A panel taller than the screen under the header stays pinned: its tabs shrink and
// the shown tab's text scrolls inside the panel, as Nour Hammour's does (Matt, 14 Sept 2026). Only
// when even that cannot fit, the rest of the panel plus a few lines of tab text, would its foot sit
// below the screen until the whole gallery had scrolled past. This element, placed inside
// .product-details, then marks the panel with data-overflows="true"; the stylesheet then sticks it
// by its bottom edge (Matt, 20 Sept 2026), using the offset set below.
// The few lines kept for the tab text before giving up on pinning: enough to read that it scrolls.
const MIN_TAB_LINES = 4;

class ProductDetailsFit extends HTMLElement {
  #observer = new ResizeObserver(() => this.#measure());
  #controller = new AbortController();

  connectedCallback() {
    const details = this.parentElement;
    if (!details) return;
    const content = details.querySelector(':scope > .group-block > .group-block-content');
    if (content) {
      for (const child of content.children) this.#observer.observe(child);
      // A shrunk tabs block keeps its height while its text changes (a variant swap), so the text
      // is watched too.
      for (const panel of content.querySelectorAll('.product-tabs__panel > *')) this.#observer.observe(panel);
    }
    this.#observer.observe(details);
    window.addEventListener('resize', () => this.#measure(), { signal: this.#controller.signal });
    this.#measure();
  }

  disconnectedCallback() {
    this.#observer.disconnect();
    this.#controller.abort();
    this.#controller = new AbortController();
  }

  #measure() {
    const details = this.parentElement;
    const group = details?.querySelector(':scope > .group-block');
    const content = group?.querySelector(':scope > .group-block-content');
    if (!details || !group || !content) return;
    // What the content needs with no free space at all: every shown child, the gaps between them
    // and the group's own padding. Read from the children, not the panel, whose height is set by
    // the screen. The tabs count as their list plus the tallest panel's text, capped at a few
    // lines: the part the stylesheet lets them shrink to. Read from scrollHeight, so the number is
    // the same whether the tabs are shrunk or not and the mark cannot flip back and forth.
    const children = Array.from(content.children).filter((c) => c instanceof HTMLElement && c.offsetParent !== null);
    const style = getComputedStyle(content);
    const gap = parseFloat(style.rowGap) || 0;
    const groupStyle = getComputedStyle(group);
    const height = (/** @type {HTMLElement} */ c) => {
      if (!c.classList.contains('product-tabs')) return c.getBoundingClientRect().height;
      const panels = Array.from(c.querySelectorAll('.product-tabs__panel'));
      const list = c.querySelector('.product-tabs__list');
      const listHeight = list ? list.getBoundingClientRect().height + (parseFloat(getComputedStyle(list).marginBlockEnd) || 0) : 0;
      const text = Math.max(0, ...panels.map((p) => p.scrollHeight));
      const lineHeight = panels[0] ? parseFloat(getComputedStyle(panels[0]).lineHeight) || 20 : 20;
      return listHeight + Math.min(text, lineHeight * MIN_TAB_LINES);
    };
    const needed =
      children.reduce((sum, c) => sum + height(c), 0) +
      gap * Math.max(0, children.length - 1) +
      (parseFloat(groupStyle.paddingTop) || 0) +
      (parseFloat(groupStyle.paddingBottom) || 0);
    const header = parseFloat(getComputedStyle(details).getPropertyValue('--header-group-height')) || 0;
    const room = window.innerHeight - header;
    const overflows = String(needed > room + 1);
    if (details.dataset.overflows !== overflows) details.dataset.overflows = overflows;
    // A panel taller than the screen sticks by its bottom edge (snippets/product-page-styles.liquid
    // reads --panel-fit-top): it scrolls with the page until its bottom reaches the bottom of the
    // screen, then holds there while the rest of the gallery scrolls past.
    if (overflows === 'true') {
      const top = Math.min(0, Math.round(window.innerHeight - details.offsetHeight));
      details.style.setProperty('--panel-fit-top', `${top}px`);
    } else {
      details.style.removeProperty('--panel-fit-top');
    }
    // A panel whose text scrolls must be reachable by keyboard. The tabs' panels already take
    // focus (tabindex="0"); a lone panel has none, so it gets one while, and only while, it scrolls.
    for (const panel of content.querySelectorAll('.product-tabs--single > .product-tabs__panel')) {
      const scrolls = panel.scrollHeight > panel.clientHeight + 1;
      if (scrolls && !panel.hasAttribute('tabindex')) {
        panel.tabIndex = 0;
        panel.dataset.fitTabindex = '';
      } else if (!scrolls && 'fitTabindex' in panel.dataset) {
        panel.removeAttribute('tabindex');
        delete panel.dataset.fitTabindex;
      }
    }
  }
}

if (!customElements.get('product-details-fit')) {
  customElements.define('product-details-fit', ProductDetailsFit);
}
